/**
 * #186 active-time tracker. Time counts only while the case is visible and the window focused, and the composer (any
 * editable field inside the case pane) is focused or being typed in, with an idle cutoff of about 2 min. The seconds
 * are heartbeated to the API, which keeps them as unlogged time per case and person (so they survive reloads and
 * devices). Every time field defaults to that tracked time and says where it came from.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { ApiError, describeError } from '../../lib/api/client'
import { type TrackedTimeOut, trackedApi, trackedKeys, trackedQuery, trackedSourceText } from './tracked-api'

export const IDLE_CUTOFF_MS = 120_000
export const TICK_MS = 1_000
export const BEAT_MS = 30_000
const MAX_BEAT_SECONDS = 300

// --- unflushed seconds per case (shared by the tracker and every time field on the page) ---------------------
const pending = new Map<string, number>()
const listeners = new Set<() => void>()
const emit = () => {
  for (const fn of [...listeners]) fn()
}
function addPending(caseUuid: string, seconds: number) {
  pending.set(caseUuid, (pending.get(caseUuid) ?? 0) + seconds)
  emit()
}
function takePending(caseUuid: string): number {
  const s = pending.get(caseUuid) ?? 0
  pending.delete(caseUuid)
  if (s) emit()
  return s
}
function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
export function pendingSeconds(caseUuid: string): number {
  return pending.get(caseUuid) ?? 0
}
/** Tests only. */
export function resetTrackerStore() {
  pending.clear()
  emit()
}

function isEditable(el: Element | null, root: HTMLElement | null): boolean {
  if (!el || !root?.contains(el)) return false
  if (el instanceof HTMLElement && el.isContentEditable) return true
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled
  return false
}

export interface TrackerOptions {
  /** The case pane: only editable fields inside it count as the composer. */
  rootRef: React.RefObject<HTMLElement | null>
  /** The heartbeat answered prompt_due: the case just passed the threshold (asked once, server-side). */
  onPromptDue?: (out: TrackedTimeOut) => void
  enabled?: boolean
}

/** Runs the tracker for one case while mounted. */
export function useActiveTimeTracker(caseUuid: string, { rootRef, onPromptDue, enabled = true }: TrackerOptions) {
  const qc = useQueryClient()
  const promptRef = React.useRef(onPromptDue)
  promptRef.current = onPromptDue
  React.useEffect(() => {
    if (!enabled || !caseUuid) return
    let lastInput = 0
    let stopped = false
    let inFlight = false
    const onActivity = (e: Event) => {
      if (isEditable(e.target as Element | null, rootRef.current)) lastInput = Date.now()
    }
    const active = () =>
      document.visibilityState === 'visible' &&
      document.hasFocus() &&
      isEditable(document.activeElement, rootRef.current) &&
      Date.now() - lastInput < IDLE_CUTOFF_MS
    const flush = async () => {
      if (stopped || inFlight) return
      const seconds = Math.min(takePending(caseUuid), MAX_BEAT_SECONDS)
      if (seconds <= 0) return
      inFlight = true
      try {
        const out = await trackedApi().heartbeat(caseUuid, seconds)
        qc.setQueryData(trackedKeys.case(caseUuid), { ...out, prompt_due: false })
        if (out.prompt_due) promptRef.current?.(out)
      } catch (err) {
        // Refused for good (billing role, merged or missing case): stop tracking. Otherwise keep the seconds.
        if (err instanceof ApiError && [403, 404, 409, 422].includes(err.status)) stopped = true
        else addPending(caseUuid, seconds)
      } finally {
        inFlight = false
      }
    }
    const tick = window.setInterval(() => {
      if (!stopped && active()) addPending(caseUuid, TICK_MS / 1000)
    }, TICK_MS)
    const beat = window.setInterval(() => void flush(), BEAT_MS)
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush()
    }
    const events = ['focusin', 'keydown', 'input', 'pointerdown'] as const
    for (const ev of events) document.addEventListener(ev, onActivity, true)
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onHide)
    return () => {
      window.clearInterval(tick)
      window.clearInterval(beat)
      for (const ev of events) document.removeEventListener(ev, onActivity, true)
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onHide)
      void flush()
      stopped = true
    }
  }, [caseUuid, enabled, qc, rootRef])
}

export interface TrackedDefault {
  seconds: number
  minutes: number
  replies: number
  /** "tracked: 7 min across 3 replies" */
  source: string
  /** Server answered (false while loading or unreachable: the field then has no tracked default). */
  known: boolean
  /** A failed read is not zero tracked time; callers show this beside the empty default. */
  error: string | null
  retry: () => void
}

/** The tracked default for a time field: the server's unlogged time plus what this page has not flushed yet. */
export function useTrackedDefault(caseUuid: string | undefined | null): TrackedDefault {
  const q = useQuery({ ...trackedQuery(caseUuid ?? ''), enabled: Boolean(caseUuid) })
  const local = React.useSyncExternalStore(
    subscribe,
    () => (caseUuid ? pendingSeconds(caseUuid) : 0),
    () => 0,
  )
  const seconds = (q.data?.seconds ?? 0) + local
  const replies = q.data?.replies ?? 0
  return {
    seconds,
    minutes: seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : 0,
    replies,
    source: trackedSourceText(seconds, replies),
    known: q.isSuccess,
    error: q.isError ? describeError(q.error) : null,
    retry: () => void q.refetch(),
  }
}

/** Time was logged on the case: the unflushed seconds are accounted for by that entry, so they are not sent. */
export function settlePending(caseUuid: string): void {
  takePending(caseUuid)
}
